import React from "react";

const HowToBuildYourBrandVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
        This lesson treats personal brand as a career advantage that opens doors and keeps them open. It offers a four step path: clarify the value and outcomes you deliver, build relationships with intent, repeat consistent signals, and show your brand through visible proof instead of claims. It adds the idea of top of mind recall, like a purple wrapper, where repeated cues make people think of you first in the moments that matter.
      </p>
    </div>
  );

  const transcript = (
        <div className="max-w-4xl mx-auto px-4 py-8 text-[17px] leading-7 text-[#323232] space-y-4 overflow-y-scroll font-gotham">

      <p>
        Everywhere you look, someone’s building a <em>personal brand</em>.<br />
        LinkedIn. Instagram. YouTube. Even your office Slack channel.
      </p>

      <p>
        Some say it gets you hired.<br />
        Some say it gets you promoted.<br />
        Well, they’re both right.
      </p>

      <p>
        It opens doors in hiring, and it keeps those doors open when it’s time for promotion.
      </p>

      <p>
        If you’re staring at your screen like I stare at Excel, not knowing what to do but pretending you do, don’t worry.
      </p>

      <p>I’ve got a 4-step roadmap just for you.</p>

      <p className="font-bold text-lg">
        Step 1: Know Your Value, Skills & What You Deliver
      </p>

      <p>
        If you don’t know what you bring to the table, you’ll end up serving whatever’s left.<br />
        Your brand starts with clarity, what do you do <em>exceptionally</em> well, and what <em>impact</em> does it create?
      </p>

      <p>
        You’re not just your job title. You’re the outcome you drive.<br />
        The difference between “I code” and “I build tools that save engineers 10 hours a week” is your brand speaking.
      </p>

      <p className="font-bold text-lg">
        Step 2: Conscious Networking
      </p>

      <p>
        Networking isn’t collecting people like Pokémon cards.<br />
        It’s about being <em>remembered</em> for the right reasons. Be curious, give before you take, and connect dots others can’t see.
      </p>

      <p>
        Real networking is when people mention your name in rooms you’re not in, not when you’ve spammed them on LinkedIn.
      </p>

      <p>
        Networking consciously is a step further and we’ll talk about that later.
      </p>

      <p className="font-bold text-lg">
        Step 3: Consistency
      </p>

      <p>
        Your brand is built in repetition. One great meeting doesn’t make a brand, a hundred consistent signals do.<br />
        How you show up in meetings, reply to emails, even how you handle stress, all of that is data.
      </p>

      <p>
        You don’t need to be perfect. Just predictably <em>you</em>.
      </p>

      <p className="font-bold text-lg">
        Step 4: Advertise Your Brand, Show, Don’t Tell
      </p>

      <p>
        Telling people you’re creative is like saying you’re funny. If you have to say it, it’s already suspicious.
      </p>

      <p>
        Show it instead. If your brand is <em>technical depth</em>, share insights, simplify complexity, teach.<br />
        If it’s <em>creativity</em>, let it reflect everywhere, how you write your out-of-office message, how you sign your emails, even the Whatsapp conversation you have.
      </p>

      <p>
        Join communities, volunteer for projects that <em>signal</em> your brand.
      </p>

      <p>
        Since you’ve made it till here, I’ll give you a bonus tip.
      </p>

      <p>
        In marketing there’s a concept called TOMA. Top-of-Mind-Awareness. This is what prompts brand recognition and recall in you.
      </p>

      <p>
        If I say I have a chocolate in a purple wrapper, you immediately think of Dairy Milk but Milka is also a purple wrapper.
        Similarly if I hold a bottle with a red sleeve? Coca-Cola right? But Sting is also red sleeved. (In fact almost every other soft drink has a red version)
      </p>

      <p>
        So then why did you think of Dairy Milk and Coca-Cola? That instinctive recall is no accident, it’s decades of consistent, deliberate branding.
      </p>

      <p>
        At work, you want the same thing. You want to be the “purple wrapper” in your stakeholder’s mental shelf.
      </p>

      <p>
        Your personal brand is like your office grapevine, it spreads fast, sticks long, and you’d better control the script.
        Be the purple wrapper and don’t settle for less!
      </p>

      <p className="font-normal">
        What’s your personal brand look like?
      </p>

    </div>
  );

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  return { about, transcript };
};

export default HowToBuildYourBrandVideo;
